import type { AxiosResponse } from 'axios';

import type {
  HandlersByPositionResponse,
  PositionByHandlerResponse,
} from '~/api/evisearch/typedefs.autogen';
import type { ExportExcelField } from '~/features/advanced-search/types';

import { endpoints as autogenEnhancements } from '~/api/evisearch/enhancements.autogen';
import { Tags } from '~/api/evisearch/tags.autogen';
import { FieldId } from '~/evifields/src/types/FieldId';
import { createBooleanTextSearchFilter } from '~/features/filters/searchFiltersUtils';
import { eviSearch } from '~/services/serviceInstances';
import { Bucket, SearchFilter } from '~/types/DashboardV2';

import type { JsonApiResource } from '../ApiTypes';
import type {
  ApiChartData,
  ChartData,
  CreateChartParams,
} from '../types/dashboard/charts';
import type {
  ExportExcelParams,
  HandlersByPositionParam,
  Links,
  PositionByHandlerParam,
  SearchAfterMeta,
  SearchAfterParams,
  SearchAfterResponseAttributes,
  SearchAfterResult,
  SearchCountParams,
  SearchCountResult,
  SearchFilterApiResponse,
  SearchMeta,
  SearchParams,
  SearchResponseResult,
  SearchResult,
  SearchType,
  SuggestionResponse,
  SuggestionsMeta,
} from './searchTypes';

import { JsonApiGetResponse, JsonApiListResponse } from '../ApiTypes';
import { toEviFilterType } from '../methods/searchV3';
import { toSortParam } from '../utils';
import { evisearchApiRoot } from './root';

type FilterSuggestionsV2Result = {
  data: JsonApiResource<'suggestion', SuggestionResponse>[];
  meta?: SuggestionsMeta;
};

type ExportOptionsAttributes = {
  fields: ExportExcelField[];
  clauses: Array<{ label: string; value: string }>;
  tables: Array<{ label: string; value: string }>;
};

type ExportOptionsResponse = {
  data?: { attributes?: ExportOptionsAttributes };
};

const convertSearchV2ToSearchFilter = (
  filterV2: SearchFilterApiResponse,
  id: FieldId,
): SearchFilter => {
  const { clause_text_search } = filterV2;
  const settings = clause_text_search
    ? { textSearchFilter: clause_text_search }
    : {};
  return {
    id,
    is_chartable: filterV2.is_chartable,
    is_sortable: filterV2.is_sortable,
    is_requestable_column: filterV2.is_requestable_column,
    operators: filterV2.operators.map((op) =>
      `${op.cardinality}` === 'infinity'
        ? { ...op, cardinality: Number.POSITIVE_INFINITY }
        : op,
    ),
    label: filterV2.label,
    section: filterV2.section,
    settings,
    type: toEviFilterType(filterV2.type),
    uf_client_field_id: filterV2.uf_client_field_id,
  };
};

// Apply the schema-derived providesTags / invalidatesTags to the raw generated
// endpoints. The consumer-facing overrides below are separate, friendly-named
// endpoints that intentionally carry no cache tags — the legacy hand-written
// methods had none, and adding them would change runtime behavior.
evisearchApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

/**
 * Consumer-facing evisearch endpoints. These reproduce the exact runtime
 * behavior of the legacy hand-written evisearch-service methods (the same
 * friendly argument and result shapes, the same request bodies/params, and the
 * same response mapping). Errors flow through untouched as the raw `AxiosError`
 * (see `baseQuery.ts`) so consumers that read `error.response.status` /
 * `error.response.data.detail` keep working, and no automatic error toast is
 * shown (the baseQuery does not default `showErrorToast`).
 */
export const evisearchApi = evisearchApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    search: build.query<SearchResult, SearchParams>({
      query: ({ page, type, pageSize, sortBy, query, filters = [] }) => ({
        url: '/search',
        method: 'post',
        body: {
          data: {
            type,
            attributes: {
              query,
              filters,
            },
          },
        },
        params: {
          'page[number]': page,
          'page[size]': pageSize,
          highlighterType: 'unified',
          sort: toSortParam(sortBy),
        },
      }),
      transformResponse: (
        response: JsonApiGetResponse<
          'search',
          SearchResponseResult,
          SearchMeta,
          void,
          Links
        >,
      ) => ({
        results: response?.data.attributes.results.map(
          (searchItem) => searchItem,
        ),
        meta: response?.meta,
      }),
    }),

    searchCount: build.query<SearchCountResult, SearchCountParams>({
      query: ({ query, type, filterId }) => ({
        url: `/search/count/${filterId}`,
        method: 'post',
        body: {
          data: {
            type,
            attributes: { query },
          },
        },
      }),
      transformResponse: (
        response: JsonApiGetResponse<SearchType, { count: number }>,
      ) => ({ count: response.data.attributes.count }),
    }),

    searchAfter: build.query<SearchAfterResult, SearchAfterParams>({
      query: ({
        type,
        query,
        filters = [],
        pageSize,
        sortBy,
        search_after,
        skip_pages,
      }) => ({
        url: '/search-after',
        method: 'post',
        body: {
          data: {
            type,
            attributes: {
              query,
              filters,
            },
          },
          ...(search_after ? { search_after } : {}),
          skip_pages,
        },
        params: {
          'page[size]': pageSize,
          highlighterType: 'unified',
          sort: toSortParam(sortBy),
        },
      }),
      transformResponse: (
        response: JsonApiGetResponse<
          'search',
          SearchAfterResponseAttributes,
          SearchAfterMeta
        >,
      ) => ({
        results: response.data.attributes.results,
        meta: response.meta!,
      }),
    }),

    getFiltersV2: build.query<
      Record<FieldId, SearchFilter>,
      { type: SearchType }
    >({
      query: ({ type }) => ({
        url: '/filters',
        method: 'get',
        params: { entity: type },
      }),
      transformResponse: (
        response: JsonApiListResponse<'filter', SearchFilterApiResponse>,
        _meta,
        arg,
      ) => {
        const filterRecord: Record<FieldId, SearchFilter> = Object.fromEntries(
          response.data.map((filter) => [
            filter.id,
            convertSearchV2ToSearchFilter(filter.attributes, filter.id),
          ]),
        );
        if (arg.type === 'document') {
          const booleanTextSearchFilter = createBooleanTextSearchFilter();
          filterRecord[booleanTextSearchFilter.id] = booleanTextSearchFilter;
        }
        return filterRecord;
      },
    }),

    getFilterSuggestionsV2: build.query<
      FilterSuggestionsV2Result,
      { filterId: string; type: SearchType; query: any }
    >({
      query: ({ filterId, type, query }) => ({
        url: `/filters/${filterId}/suggestions`,
        method: 'get',
        params: { entity: type, query },
      }),
      transformResponse: (
        response: JsonApiListResponse<
          'suggestion',
          SuggestionResponse,
          SuggestionsMeta,
          void
        >,
      ) => ({
        data: response.data,
        meta: response.meta,
      }),
      // Tag per filter id so a downstream mutation can invalidate a specific
      // filter's suggestions and force a refetch (e.g. a UF field value save,
      // where filterId is the UF field UUID).
      providesTags: (_result, _error, { filterId }) => [
        { type: Tags.FilterSuggestion, id: filterId },
      ],
    }),

    getChartDataV2: build.query<ChartData, CreateChartParams>({
      query: ({ uuid, query, bucket_size = 10, entity, interval }) => ({
        url: `/search/chart/${uuid}`,
        method: 'post',
        body: {
          data: {
            type: entity,
            attributes: { query, bucket_count: bucket_size, interval },
          },
        },
      }),
      transformResponse: (
        response: JsonApiGetResponse<SearchType, ApiChartData>,
      ): ChartData => {
        const { buckets: apiBuckets, bucket_count: bucketCount } =
          response!.data.attributes;
        return {
          bucketCount,
          buckets: apiBuckets.map(
            (bucket): Bucket => ({
              label: bucket.label,
              value: bucket.count,
              bucketValue: bucket.value,
            }),
          ),
          meta: response?.meta,
        };
      },
    }),

    getSearchExportOptionsV2: build.query<
      ExportOptionsAttributes,
      { query: Array<any>; type: SearchType }
    >({
      query: ({ query, type }) => ({
        url: '/search/export-options',
        method: 'post',
        body: { data: { attributes: { query, type } } },
      }),
      transformResponse: (response: ExportOptionsResponse) =>
        response.data?.attributes ?? { fields: [], clauses: [], tables: [] },
    }),

    exportExcelV3: build.mutation<
      AxiosResponse<ArrayBuffer>,
      ExportExcelParams
    >({
      // Done as a direct `queryFn` (bypassing the baseQuery) so the
      // `responseType: 'arraybuffer'` + `fullResponse: true` config — which
      // returns the full `AxiosResponse` rather than the unwrapped body — is
      // expressed where its literal types are statically known.
      queryFn: async ({ type, query, fields = [] }) => {
        try {
          const response = await eviSearch.post(
            '/search/export',
            { data: { attributes: { query, type, fields } } },
            { responseType: 'arraybuffer', fullResponse: true },
          );
          return { data: response };
        } catch (error) {
          return { error };
        }
      },
    }),

    validateBooleanQueryEvisearch: build.query<{ detail: string }, string>({
      query: (query) => ({
        url: '/validate-bool-query',
        method: 'get',
        params: { query },
      }),
      transformResponse: (response: { detail: string }) => response,
    }),

    getPositionByHandler: build.query<
      PositionByHandlerResponse,
      PositionByHandlerParam
    >({
      query: ({ handler_id, query, sort }) => ({
        url: '/position-by-handler',
        method: 'post',
        body: {
          data: {
            attributes: {
              handler_id: Number(handler_id),
              query,
              sort,
            },
          },
        },
      }),
      transformResponse: (response: PositionByHandlerResponse) => response,
    }),

    getHandlersByPosition: build.query<
      HandlersByPositionResponse,
      HandlersByPositionParam
    >({
      query: ({ position, query, sort }) => ({
        url: '/handlers-by-position',
        method: 'post',
        body: {
          data: {
            attributes: {
              position,
              query,
              sort,
            },
          },
        },
      }),
      transformResponse: (response: HandlersByPositionResponse) => response,
    }),
  }),
});
